import { FREQUENCY, ONCE_SCHEDULE_DATE_OPTION, SCHEDULE_TYPE } from '@flexible-reports/utils/schedule-constants';
import { DAYS_IN_WEEK } from '@urc-ui/utils/types';
import { addDays, addMonths, format, add, isSameDay, isPast } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getCoercedTimezone } from '@flexible-reports/utils/timezone/get-coerced-timezone';
import { ReportSchedule } from '@flexible-reports/store/slices/types';

export const DATE_FORMAT = 'yyyy-MM-dd';
/**
 * Returns the default schedule for a given frequency and date option
 * @param frequency ONCE, DAILY, WEEKLY, MONTHLY
 * @param dateOption NOW, FUTURE
 */
export const getDefaultReportScheduleForFrequency = (frequency: FREQUENCY, dateOption?: ONCE_SCHEDULE_DATE_OPTION): ReportSchedule => {
  if (frequency === FREQUENCY.ONCE) {
    return getDefaultOnceSubscriptionSchedule(dateOption);
  } else if (frequency === FREQUENCY.DAILY) {
    return getDefaultDailySubscriptionSchedule();
  } else if (frequency === FREQUENCY.WEEKLY) {
    return getDefaultWeeklySubscriptionSchedule();
  } else if (frequency === FREQUENCY.MONTHLY) {
    return getDefaultMonthlySubscriptionSchedule();
  }

  throw Error(`Invalid Frequency: ${frequency}`);
};

const getDefaultOnceSubscriptionSchedule = (dateOption?: ONCE_SCHEDULE_DATE_OPTION): ReportSchedule => {
  if (dateOption === ONCE_SCHEDULE_DATE_OPTION.TODAY) {
    return getDefaultOnceNowSubscriptionSchedule();
  }
  if (dateOption === ONCE_SCHEDULE_DATE_OPTION.FUTURE) {
    return getDefaultOnceFutureSubscriptionSchedule();
  }
  return getDefaultOnceNowSubscriptionSchedule();
};

const getDefaultOnceNowSubscriptionSchedule = (): ReportSchedule => {
  return {
    frequency: FREQUENCY.ONCE,
    onceDateOption: ONCE_SCHEDULE_DATE_OPTION.TODAY
  };
};

const getDefaultOnceFutureSubscriptionSchedule = (): ReportSchedule => {
  return {
    frequency: FREQUENCY.ONCE,
    onceDateOption: ONCE_SCHEDULE_DATE_OPTION.FUTURE,
    runDate: format(getOneMonthFromNow(), DATE_FORMAT),
    hour: (getCurrentHour() + 1) % 24,
    timezone: getCoercedTimezone()
  };
};

const getDefaultDailySubscriptionSchedule = (): ReportSchedule => {
  return {
    frequency: FREQUENCY.DAILY,
    hour: getCurrentHour(),
    endDate: format(getOneMonthFromNow(), DATE_FORMAT),
    timezone: getCoercedTimezone()
  };
};

const getDefaultWeeklySubscriptionSchedule = (): ReportSchedule => {
  return {
    frequency: FREQUENCY.WEEKLY,
    daysOfWeek: [DAYS_IN_WEEK.MONDAY],
    hour: getCurrentHour(),
    endDate: format(getOneMonthFromNow(), DATE_FORMAT),
    timezone: getCoercedTimezone()
  };
};

const getDefaultMonthlySubscriptionSchedule = (): ReportSchedule => {
  const currentDayOfMonth = getCurrentDayOfMonth();
  if (currentDayOfMonth > 28) {
    return {
      frequency: FREQUENCY.MONTHLY,
      lastDayOfMonth: true,
      hour: getCurrentHour(),
      endDate: format(getOneMonthFromNow(), DATE_FORMAT),
      timezone: getCoercedTimezone()
    };
  }
  return {
    frequency: FREQUENCY.MONTHLY,
    dayOfMonth: currentDayOfMonth,
    hour: getCurrentHour(),
    endDate: format(getOneMonthFromNow(), DATE_FORMAT),
    timezone: getCoercedTimezone()
  };
};

// Returns One Month From Now using the default browser timezone
export const getOneMonthFromNow = (): Date => {
  return addMonths(new Date(), 1);
};

// Returns the Current Hour in current browser timezone
export const getCurrentHour = (): number => {
  return new Date().getHours();
};

// Returns the Current Hour in specified timezone
export const getCurrentHourInTimezone = (timezone: string): number => {
  const date = new Date();
  const zonedDate = utcToZonedTime(date, timezone);
  return zonedDate.getHours();
};

// Returns if the date is same as current day
export const isSameDayAsToday = (date: string, timezone: string): boolean => {
  return isSameDay(utcToZonedTime(date, timezone), getTodayInTimezone(timezone));
};

// Returns the Current Day Of Month in current browser timezone
export const getCurrentDayOfMonth = (): number => {
  return new Date().getDate();
};

// Returns Tomorrows Date in mentioned timezone
export const getTomorrowInTimezone = (timezone: string): Date => {
  return addDays(utcToZonedTime(new Date(), timezone), 1);
};

// Returns Date after One year in mentioned timezone
export const getDateOneYearFromNowInTimezone = (timezone: string): Date => {
  return add(utcToZonedTime(new Date(), timezone), { years: 1 });
};

// Returns Todays Date in mentioned timezone
export const getTodayInTimezone = (timezone: string): Date => {
  return utcToZonedTime(new Date(), timezone);
};

// Returns Days Of Week from Schedule Object For Daily Subscription
export const getDaysOfWeekFromSchedule = (schedule: ReportSchedule): DAYS_IN_WEEK[] => {
  if (schedule.daysOfWeek && schedule.daysOfWeek.length > 0) {
    return schedule.daysOfWeek.map(day => DAYS_IN_WEEK[day as DAYS_IN_WEEK]);
  }
  return [];
};

/**
 * This function returns the current day of month with a max value of 28.
 */
export const getCoercedDayOfMonth = (): number => {
  const currentDayOfMonth = getCurrentDayOfMonth();
  return currentDayOfMonth > 28 ? 28 : currentDayOfMonth;
};

/**
 * This function returns the schedule type based on the schedule object.
 * It returns either of IMMEDIATE oR SCHEDULED
 * The output of this function is used to determine the label of create subscription button
 * It is also used to determine if a spoofer can create subscription based on schedule options
 * @param schedule
 */
export const getScheduleType = (schedule: ReportSchedule): SCHEDULE_TYPE => {
  if (schedule.frequency === FREQUENCY.ONCE) {
    return schedule.onceDateOption === ONCE_SCHEDULE_DATE_OPTION.TODAY ? SCHEDULE_TYPE.IMMEDIATE : SCHEDULE_TYPE.SCHEDULED;
  }
  return SCHEDULE_TYPE.SCHEDULED;
};

export const isScheduledReport = (type: SCHEDULE_TYPE): boolean => {
  return type === SCHEDULE_TYPE.SCHEDULED;
};

export const isDateInPast = (dateString: string): boolean => {
  const date = new Date(dateString);
  return isPast(date);
};
